/**
 * =========================================================
 * Social Media Hub - Premium Sidenav
 * =========================================================
 *
 * Modern, sleek sidebar with premium navigation experience,
 * smooth animations, and polished visual design.
 */

import { useEffect } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
          sx={{
            fontSize: "0.65rem",
            letterSpacing: "0.1em",
            opacity: 0.5,
            fontWeight: 600,
          }}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
          sx={{
            my: 1.5,
            opacity: 0.08,
            mx: 2,
          }}
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      {/* Brand Header */}
      <MDBox pt={3} pb={2} px={3}>
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{
            cursor: "pointer",
            minWidth: "44px",
            minHeight: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            touchAction: "manipulation",
            borderRadius: "12px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          sx={{ touchAction: "manipulation" }}
        >
          {/* Premium Brand Icon with Gradient */}
          <MDBox
            sx={{
              width: 44,
              height: 44,
              borderRadius: "14px",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(99, 102, 241, 0.35), 0 4px 12px rgba(139, 92, 246, 0.25)",
              mr: 2,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow:
                  "0 12px 32px rgba(99, 102, 241, 0.4), 0 6px 16px rgba(139, 92, 246, 0.3)",
              },
            }}
          >
            <Icon sx={{ color: "white", fontSize: "1.4rem !important" }}>hub</Icon>
          </MDBox>
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="bold"
              color={textColor}
              sx={{
                fontSize: "1.1rem",
                letterSpacing: "-0.02em",
                fontWeight: 700,
              }}
            >
              {brandName || "Social Hub"}
            </MDTypography>
            {!miniSidenav && (
              <MDTypography
                variant="caption"
                sx={{
                  display: "block",
                  fontSize: "0.7rem",
                  color: "rgba(0, 0, 0, 0.4)",
                  mt: -0.3,
                  letterSpacing: "0.02em",
                }}
              >
                Content Management
              </MDTypography>
            )}
          </MDBox>
        </MDBox>
      </MDBox>

      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
        sx={{
          mx: 3,
          opacity: 0.08,
        }}
      />

      {/* Navigation Links */}
      <List sx={{ px: 1.5, py: 1 }}>{renderRoutes}</List>

      {/* Premium Bottom Section */}
      <MDBox
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2.5,
          borderTop: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.06)",
          display: { xs: "none", xl: "block" },
          background: "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.8) 100%)",
        }}
      >
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Chip
            label="Pro"
            size="small"
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              color: "white",
              fontSize: "0.65rem",
              fontWeight: 600,
              height: "20px",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />
        </MDBox>
        <MDTypography
          variant="caption"
          sx={{
            color: "rgba(0, 0, 0, 0.35)",
            fontSize: "0.65rem",
            display: "block",
            textAlign: "center",
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          Social Media Hub v1.0
        </MDTypography>
        <MDTypography
          variant="caption"
          sx={{
            color: "rgba(0, 0, 0, 0.25)",
            fontSize: "0.6rem",
            display: "block",
            textAlign: "center",
            mt: 0.5,
          }}
        >
          © 2024 All rights reserved
        </MDTypography>
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "primary",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
